import React, { useState, useEffect } from 'react';
import { Search, Bell, HelpCircle, LayoutDashboard, UserCog, UserCheck, Film, Ticket, LogOut, Menu } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminRoleManagement from './AdminRoleManagement';
import AdminOrganizerRequests from './AdminOrganizerRequests';
import AdminManageEvents from './AdminManageEvents';
import AdminManageBookings from './AdminManageBookings';
import { supabase } from '../lib/supabase';

export default function AdminPanel({ onClose }: { onClose: () => void }) {
  const [activeSection, setActiveSection] = useState<'dashboard' | 'role-management' | 'organizer-requests' | 'manage-events' | 'manage-bookings'>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardDays, setDashboardDays] = useState<number>(30)
  const [dashboardOrganizerId, setDashboardOrganizerId] = useState<number | null>(null)
  const [dashboardMovieId, setDashboardMovieId] = useState<number | null>(null)
  const [organizerOptions, setOrganizerOptions] = useState<Array<{ id: number; name: string }>>([])
  const [movieOptions, setMovieOptions] = useState<Array<{ id: number; title: string }>>([])
  const [authorized, setAuthorized] = useState<boolean | null>(null)

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser()
      const uid = data.user?.id || null
      if (!uid) { setAuthorized(false); return }
      const { data: profile } = await supabase.from('profiles').select('role').eq('id', uid).maybeSingle()
      const role = (profile as { role?: string } | null)?.role || null
      setAuthorized(role === 'admin')
    })()
  }, [])

  React.useEffect(() => {
    (async () => {
      if (activeSection !== 'dashboard') return
      const { data: orgs } = await (await import('../lib/supabase')).supabase.from('organizers').select('id,name').order('name')
      setOrganizerOptions(((orgs || []) as any[]))
    })()
  }, [activeSection])

  React.useEffect(() => {
    (async () => {
      if (activeSection !== 'dashboard') return
      if (dashboardOrganizerId) {
        const { data: ms } = await (await import('../lib/supabase')).supabase.from('movies').select('id,title').eq('organizer_id', dashboardOrganizerId).order('created_at', { ascending: false })
        setMovieOptions(((ms || []) as any[]))
      } else {
        setMovieOptions([])
      }
    })()
  }, [activeSection, dashboardOrganizerId])

  return (
    <div className="pt-20">
      {authorized === false ? (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Access Denied</h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">You do not have permission to access this page.</p>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">Back</button>
          </div>
        </div>
      ) : (
      <div className="relative flex min-h-[calc(100vh-80px)] w-full">
        <aside className="sticky top-20 h-[calc(100vh-80px)] w-64 flex-shrink-0 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 hidden lg:flex flex-col justify-between p-4">
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-2 px-3 text-indigo-400">
              <LayoutDashboard className="h-6 w-6" />
              <h2 className="text-gray-900 dark:text-white text-lg font-bold">Admin Panel</h2>
            </div>
          <div className="flex flex-col gap-2">
              <button
                onClick={() => setActiveSection('dashboard')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='dashboard' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                <LayoutDashboard className="h-4 w-4" />
                <p className="text-sm font-medium">Dashboard</p>
              </button>
              <button
                onClick={() => setActiveSection('role-management')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='role-management' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                <UserCog className="h-4 w-4" />
                <p className="text-sm font-medium">Role Management</p>
              </button>
              <button
                onClick={() => setActiveSection('organizer-requests')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='organizer-requests' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                <UserCheck className="h-4 w-4" />
                <p className="text-sm font-medium">Organizer Requests</p>
              </button>
              <button
                onClick={() => setActiveSection('manage-events')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='manage-events' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                <Film className="h-4 w-4" />
                <p className="text-sm font-medium">Manage Events</p>
              </button>
              <button
                onClick={() => setActiveSection('manage-bookings')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='manage-bookings' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                <Ticket className="h-4 w-4" />
                <p className="text-sm font-medium">Manage Bookings</p>
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="w-full">
            <button onClick={onClose} className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg">
              <LogOut className="h-4 w-4" />
              <p className="text-sm font-medium text-gray-900 dark:text-white">Back</p>
            </button>
            </div>
          </div>
        </aside>
        {sidebarOpen && (
          <div className="fixed inset-0 z-30 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <aside className="absolute top-20 left-0 bottom-0 w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col justify-between p-4">
              <div className="flex flex-col gap-8">
                <div className="flex items-center gap-2 px-3 text-indigo-400">
                  <LayoutDashboard className="h-6 w-6" />
                  <h2 className="text-gray-900 dark:text-white text-lg font-bold">Admin Panel</h2>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => { setActiveSection('dashboard'); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='dashboard' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <p className="text-sm font-medium">Dashboard</p>
                  </button>
                  <button
                    onClick={() => { setActiveSection('role-management'); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='role-management' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  >
                    <UserCog className="h-4 w-4" />
                    <p className="text-sm font-medium">Role Management</p>
                  </button>
                  <button
                    onClick={() => { setActiveSection('organizer-requests'); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='organizer-requests' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  >
                    <UserCheck className="h-4 w-4" />
                    <p className="text-sm font-medium">Organizer Requests</p>
                  </button>
                  <button
                    onClick={() => { setActiveSection('manage-events'); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='manage-events' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  >
                    <Film className="h-4 w-4" />
                    <p className="text-sm font-medium">Manage Events</p>
                  </button>
                  <button
                    onClick={() => { setActiveSection('manage-bookings'); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection==='manage-bookings' ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  >
                    <Ticket className="h-4 w-4" />
                    <p className="text-sm font-medium">Manage Bookings</p>
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="w-full">
                <button onClick={() => { setSidebarOpen(false); onClose(); }} className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg">
                  <LogOut className="h-4 w-4" />
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Back</p>
                </button>
                </div>
              </div>
            </aside>
          </div>
        )}
        <main className="flex-1 flex flex-col">
            <div className="z-10 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-3 bg-gray-100 dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="flex items-center justify-center rounded-full h-10 w-10 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white lg:hidden">
                <Menu className="h-5 w-5" />
              </button>
              <label className="relative flex-1 max-w-sm">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                <input className="form-input w-full rounded-lg text-gray-900 dark:text-white focus:outline-0 focus:ring-2 focus:ring-indigo-600/50 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 placeholder:text-gray-500 dark:placeholder:text-gray-400 pl-9 pr-4 text-sm" placeholder="Search anything..." />
              </label>
            </div>
            <div className="flex items-center gap-3">
              {activeSection === 'dashboard' && (
                <div className="flex items-center gap-3">
                  <select
                    value={dashboardDays}
                    onChange={e => setDashboardDays(Number(e.target.value))}
                    className="h-10 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                  >
                    <option value={1}>Today</option>
                    <option value={7}>Last 7 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={90}>Last 90 Days</option>
                  </select>
                  <select
                    value={dashboardOrganizerId ?? ''}
                    onChange={e => { const v = e.target.value ? Number(e.target.value) : null; setDashboardOrganizerId(v); setDashboardMovieId(null); }}
                    className="h-10 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                  >
                    <option value="">All Organizers</option>
                    {organizerOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  <select
                    value={dashboardMovieId ?? ''}
                    onChange={e => setDashboardMovieId(e.target.value ? Number(e.target.value) : null)}
                    className="h-10 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                  >
                    <option value="">All Events</option>
                    {movieOptions.map(m => (
                      <option key={m.id} value={m.id}>{m.title}</option>
                    ))}
                  </select>
                </div>
              )}
              <button className="flex items-center justify-center rounded-full h-10 w-10 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white">
                <Bell className="h-5 w-5" />
              </button>
              <button className="flex items-center justify-center rounded-full h-10 w-10 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white">
                <HelpCircle className="h-5 w-5" />
              </button>
              <button onClick={onClose} className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200">×</button>
            </div>
          </div>
          <div className="p-6 space-y-8">
            {activeSection === 'dashboard' && (<AdminDashboard days={dashboardDays} organizerId={dashboardOrganizerId} movieId={dashboardMovieId} />)}
            {activeSection === 'role-management' && (<AdminRoleManagement />)}
            {activeSection === 'organizer-requests' && (<AdminOrganizerRequests />)}
            {activeSection === 'manage-events' && (<AdminManageEvents />)}
            {activeSection === 'manage-bookings' && (<AdminManageBookings />)}
          </div>
        </main>
      </div>
      )}
    </div>
  );
}

